import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { excludeProps } from '@shared/theme/util';
import { merge } from 'lodash-es';
import type { ReactNode } from 'react';
import type { InterpolationValues, Translation } from '@/feature/lang/types';
import { Icon } from '@/icon/Icon';
import Translate from '../Translate';
import { useConfig } from './useConfig';

const Container = styled('div', { name: 'Empty', slot: 'Container' })({
  display: 'flex',
  flexFlow: 'column nowrap',
  height: '100%',
  width: '100%',
  container: 'empty / inline-size',
});
const SpaceBefore = styled('div', { name: 'Empty', slot: 'SpaceAbove' })({
  flex: 3,
});
const SpaceAfter = styled('div', { name: 'Empty', slot: 'SpaceAfter' })({
  flex: 4,
});
const Content = styled('div', {
  name: 'Empty',
  slot: 'Content',
  shouldForwardProp: (propName) => excludeProps(propName, ['margin']),
})<{ margin: EmptyMargin }>(({ theme, margin }) => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 0,
  gap: theme.spacing(1),
  margin: margin === 'small' ? theme.spacing(1) : theme.spacing(3, 4),
}));
const Text = styled(Typography, { name: 'Empty', slot: 'Text' })(
  ({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    textAlign: 'center',
    '@container empty (min-width: 400px)': {
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
    },
  })
);
const Actions = styled('div', {
  name: 'Empty',
  slot: 'Actions',
  shouldForwardProp: (propName) => excludeProps(propName, ['margin']),
})<{ margin: EmptyMargin }>(({ theme, margin }) => ({
  margin: theme.spacing(margin === 'small' ? 1 : 2),
}));

export type EmptyType =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'invalid_selection'
  | 'no_content'
  | 'no_data_list'
  | 'no_data_chart'
  | 'no_permit';

type EmptyMargin = 'small' | 'normal';

export interface EmptyProps {
  type?: EmptyType;
  text?: Translation;
  actions?: ReactNode;
  textValues?: InterpolationValues;
  margin?: EmptyMargin;
}

const Empty = ({
  type = 'no_content',
  text,
  textValues,
  actions,
  margin = 'normal',
}: EmptyProps) => {
  const config = useConfig(type);
  const info = merge({}, config, { text, actions });

  if (!config) {
    return null;
  }

  return (
    <Container>
      <SpaceBefore />
      <Content margin={margin}>
        <Icon name={config.icon} size="xxlarge" color="action.active" />
        {info.text && (
          <Text variant="body2">
            <Translate text={info.text} values={textValues} />
          </Text>
        )}
        {info.actions && <Actions margin={margin}>{info.actions}</Actions>}
      </Content>
      <SpaceAfter />
    </Container>
  );
};

export default Empty;
